import React, { useMemo } from 'react';
import { Alert, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import Loader from '../../../components/Loader';
import InspectionDamagePanel from './InspectionDamagePanel';
import type { InspectionDamage, VinInspectionItem } from '../inspectionTypes';
import {
  isInspectionBaseView,
  isMobileInspectionTerminal,
  MOBILE_VIEW_NAME,
  normalizeInspectionViewName,
  orderInspectionViews,
} from '../inspectionViewConstants';
import type { ApiError } from '../../../types/api/network';
import type { InspectionMediaData, InspectionMediaType } from '../inspectionMediaTypes';

interface InspectionDamageTabContentProps {
  inspection: VinInspectionItem;
  damages: InspectionDamage[];
  loading: boolean;
  error?: ApiError | null;
  onRetry?: () => void;
  onOpenModal?: (imageData?: InspectionMediaData, type?: InspectionMediaType) => void;
}

const InspectionDamageTabContent: React.FC<InspectionDamageTabContentProps> = ({
  inspection,
  damages,
  loading,
  error,
  onRetry,
  onOpenModal,
}) => {
  const isMobileInspection = isMobileInspectionTerminal(inspection?.terminal);

  const viewNames = useMemo(() => {
    if (isMobileInspection) {
      return damages.length ? [MOBILE_VIEW_NAME] : [];
    }

    const uniqueViews = damages
      .map(item => normalizeInspectionViewName(item?.viewName))
      .filter(Boolean);

    return orderInspectionViews(uniqueViews);
  }, [damages, isMobileInspection]);

  const hasBaseViewDefectImages = useMemo(() => {
    return damages.some(item => {
      const view = normalizeInspectionViewName(item?.viewName);
      return isInspectionBaseView(view) && Boolean(item?.newMetadata || item?.metadata);
    });
  }, [damages]);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="p-4">
        <Alert
          message="Failed to load defect images"
          description={error?.message || 'An unexpected error occurred'}
          type="error"
          showIcon
          action={
            onRetry && (
              <Button size="small" icon={<ReloadOutlined />} onClick={onRetry} className="ml-2">
                Retry
              </Button>
            )
          }
        />
      </div>
    );
  }

  if (!damages.length) {
    return <div className="text-center text-gray-400 py-10">No defects were identified during the inspection.</div>;
  }

  return (
    <InspectionDamagePanel
      inspection={inspection}
      viewNames={viewNames}
      damages={damages}
      onOpenModal={onOpenModal}
      isMobileInspection={isMobileInspection}
      expandAllViews={hasBaseViewDefectImages}
    />
  );
};

export default InspectionDamageTabContent;
